import React, { useState, useRef, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';

const ASSISTANT_ID = 'asst_MS46Onoy2HfAhEMwuNf4LeJl';
const HF_VECTOR_STORE_ID = 'vs_69c95c269b808191b43fcd250abe1743'; // HF Manuals (attached at assistant level)
const LT_VECTOR_STORE_ID = 'vs_69c95de7a6e881919534601cf13058e4'; // L&T Manuals (attached at thread level)

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;
const EDGE_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/openai-assistants`;

const openaiRequest = async (path, method = 'GET', body = null) => {
  const res = await fetch(EDGE_FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ path, method, body }),
  });
  if (!res?.ok) {
    const err = await res?.json()?.catch(() => ({}));
    throw new Error(err?.error?.message || err?.error || `API error: ${res.status}`);
  }
  const data = await res?.json();
  if (data?.error) {
    throw new Error(data.error?.message || data.error);
  }
  return data;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const pollRun = async (threadId, runId, onStatus) => {
  let run;
  do {
    await sleep(1200);
    run = await openaiRequest(`/threads/${threadId}/runs/${runId}`);
    onStatus?.(run?.status);
  } while (['queued', 'in_progress', 'cancelling']?.includes(run?.status));
  return run;
};

const extractCitations = (text, annotations) => {
  if (!annotations?.length) return { cleanText: text, citations: [] };
  let cleanText = text;
  const citations = [];
  annotations?.forEach((ann, idx) => {
    if (ann?.type === 'file_citation') {
      cleanText = cleanText?.replace(ann?.text, ` [${idx + 1}]`);
      citations?.push({ index: idx + 1, fileId: ann?.file_citation?.file_id, quote: ann?.file_citation?.quote });
    } else if (ann?.type === 'file_path') {
      cleanText = cleanText?.replace(ann?.text, ` [${idx + 1}]`);
      citations?.push({ index: idx + 1, fileId: ann?.file_path?.file_id });
    }
  });
  return { cleanText, citations };
};

const MessageBubble = ({ msg }) => {
  const isUser = msg?.role === 'user';
  const { cleanText, citations } = extractCitations(msg?.content, msg?.annotations);

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${isUser ? 'bg-blue-600' : 'bg-gradient-to-br from-purple-600 to-blue-500'}`}>
        {isUser ? <Icon name="User" size={14} /> : <Icon name="Zap" size={14} />}
      </div>
      {/* Bubble */}
      <div className={`max-w-[75%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-blue-600 text-white rounded-tr-sm' :'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-tl-sm shadow-sm'
        }`}>
          {cleanText}
        </div>

        {/* Citations */}
        {citations?.length > 0 && (
          <div className="mt-1 space-y-1">
            {citations?.map((c) => (
              <div key={c?.index} className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-semibold">{c?.index}</span>
                <span className="italic">{c?.quote ? `"${c?.quote?.slice(0, 120)}${c?.quote?.length > 120 ? '…' : ''}"` : `Source file: ${c?.fileId}`}</span>
              </div>
            ))}
          </div>
        )}

        <span className="text-[10px] text-gray-400 dark:text-gray-500 px-1">
          {msg?.timestamp}
        </span>
      </div>
    </div>
  );
};

const TypingIndicator = ({ status }) => (
  <div className="flex gap-3 mb-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-blue-500 flex items-center justify-center">
      <Icon name="Zap" size={14} className="text-white" />
    </div>
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">{status || 'thinking'}…</span>
      </div>
    </div>
  </div>
);

const SmartFix = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [runStatus, setRunStatus] = useState('');
  const [threadId, setThreadId] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const autoResize = () => {
    const ta = textareaRef?.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta?.scrollHeight, 160) + 'px';
    }
  };

  const sendMessage = useCallback(async () => {
    const text = input?.trim();
    if (!text || isLoading) return;

    setError(null);
    setInput('');
    if (textareaRef?.current) textareaRef.current.style.height = 'auto';

    const timestamp = new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setMessages((prev) => [...prev, { role: 'user', content: text, timestamp }]);
    setIsLoading(true);
    setRunStatus('queued');

    try {
      let currentThreadId = threadId;

      // First message: create thread with L&T vector store attached
      if (!currentThreadId) {
        const thread = await openaiRequest('/threads', 'POST', {
          tool_resources: {
            file_search: {
              vector_store_ids: [LT_VECTOR_STORE_ID],
            },
          },
        });
        currentThreadId = thread?.id;
        setThreadId(currentThreadId);
      }

      // Add user message to thread
      await openaiRequest(`/threads/${currentThreadId}/messages`, 'POST', {
        role: 'user',
        content: text,
      });

      // Create a run against the assistant
      let run = await openaiRequest(`/threads/${currentThreadId}/runs`, 'POST', {
        assistant_id: ASSISTANT_ID,
      });

      // Poll until run completes
      const completedRun = await pollRun(currentThreadId, run?.id, setRunStatus);

      if (completedRun?.status !== 'completed') {
        throw new Error(`Run ended with status: ${completedRun.status}. ${completedRun.last_error?.message || ''}`);
      }

      // Fetch the latest assistant message
      const messagesRes = await openaiRequest(`/threads/${currentThreadId}/messages?limit=1&order=desc`);
      const assistantMsg = messagesRes?.data?.[0];

      if (assistantMsg?.role === 'assistant') {
        const contentBlock = assistantMsg?.content?.[0];
        const rawText = contentBlock?.text?.value || '';
        const annotations = contentBlock?.text?.annotations || [];
        const replyTimestamp = new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: rawText, annotations, timestamp: replyTimestamp },
        ]);
      }
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
      setRunStatus('');
    }
  }, [input, isLoading, threadId]);

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      sendMessage();
    }
  };

  const handleNewSession = () => {
    setMessages([]);
    setThreadId(null);
    setError(null);
    setInput('');
  };

  const apiKeyMissing = !SUPABASE_URL || !SUPABASE_ANON_KEY;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Header />
      <div className="container mx-auto px-6 lg:px-8 py-4 flex flex-col flex-1" style={{ maxHeight: 'calc(100vh - 64px)' }}>
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: 'SmartFix', path: '/smart-fix' },
          ]}
        />

        {/* Page Header */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Icon name="Zap" size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">SmartFix AI Assistant</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Tyre curing press troubleshooting · HF &amp; L&amp;T manuals
              </p>
            </div>
          </div>
          {messages?.length > 0 && (
            <button
              onClick={handleNewSession}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Icon name="RotateCcw" size={15} />
              New Session
            </button>
          )}
        </div>

        {/* Chat Container */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700" style={{ minHeight: 0 }}>

          {/* API Key Warning */}
          {apiKeyMissing && (
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
              <Icon name="AlertTriangle" size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                OpenAI API key not configured. Please ensure <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded text-xs">VITE_OPENAI_API_KEY</code> is set in your environment.
              </p>
            </div>
          )}

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto px-4 py-4" style={{ minHeight: 0 }}>
            {messages?.length === 0 && !isLoading && (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg">
                  <Icon name="Wrench" size={28} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
                  Ready to Troubleshoot
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
                  Describe the fault, error code, or issue with your tyre curing press. Specify the brand (HF or L&amp;T) for best results.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-md">
                  {[
                    'HF press showing fault code E-12, what does it mean?',
                    'L&T press hydraulic pressure dropping during cure cycle',
                    'Press not reaching set temperature on HF machine',
                    'L&T mold not closing fully — mechanical or hydraulic issue?',
                  ]?.map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => { setInput(suggestion); textareaRef?.current?.focus(); }}
                      className="text-left px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages?.map((msg, idx) => (
              <MessageBubble key={idx} msg={msg} />
            ))}

            {isLoading && <TypingIndicator status={runStatus} />}

            {error && (
              <div className="flex items-start gap-3 mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Thread indicator */}
          {threadId && (
            <div className="px-4 py-1.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
                Thread: {threadId} · HF + L&amp;T vector stores active
              </p>
            </div>
          )}

          {/* Input Area */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => { setInput(e?.target?.value); autoResize(); }}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe the fault or ask about your press… (Enter to send, Shift+Enter for new line)"
                  disabled={isLoading || apiKeyMissing}
                  rows={1}
                  className="w-full resize-none px-4 py-3 pr-12 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ minHeight: '48px', maxHeight: '160px' }}
                />
              </div>
              <button
                onClick={sendMessage}
                disabled={!input?.trim() || isLoading || apiKeyMissing}
                className="flex-shrink-0 w-11 h-11 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center transition-colors shadow-sm"
              >
                {isLoading
                  ? <Icon name="Loader2" size={18} className="text-white animate-spin" />
                  : <Icon name="Send" size={18} className="text-white" />
                }
              </button>
            </div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 px-1">
              Powered by OpenAI Assistants API · Searches HF &amp; L&amp;T manuals
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartFix;